import { paymentOnboardingResponseSchema, type PaymentOnboardingResponse } from "@teamna/shared";
import { useMutation, useQuery, useQueryClient, type UseMutationResult, type UseQueryResult } from "@tanstack/react-query";
import { api } from "../api/client.ts";
import { queryKeys } from "./keys.ts";

/**
 * The business's own payment account (Stripe Connect), for the Get paid page and the Payments page.
 * The API reads the state from Stripe; these hooks only ask for it. Decision record
 * `teamna/decisions/2026-10-05-stripe-connect.md`.
 */

const base = (businessId: string) => `/owner/businesses/${businessId}/payment-onboarding`;

/** GET .../payment-onboarding: the state as Teamna last read it. Changes nothing at Stripe. */
export function usePayoutAccount(businessId: string | null): UseQueryResult<PaymentOnboardingResponse> {
  return useQuery({
    queryKey: queryKeys.owner.payoutAccount(businessId ?? "none"),
    queryFn: () => api.get(base(businessId ?? ""), paymentOnboardingResponseSchema),
    enabled: businessId !== null,
    staleTime: 30 * 1000,
  });
}

/**
 * POST .../payment-onboarding/link: makes the account the first time, then a fresh link to Stripe's
 * form. The link works once and expires in 5 minutes, so open it at once. Owner only.
 */
export function useStartPayoutOnboarding(businessId: string): UseMutationResult<PaymentOnboardingResponse, unknown, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post(`${base(businessId)}/link`, {}, paymentOnboardingResponseSchema),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.owner.payoutAccount(businessId), { ...data, onboardingUrl: null });
    },
  });
}

/** POST .../payment-onboarding/refresh: Teamna reads the account from Stripe now. */
export function useRefreshPayoutAccount(businessId: string): UseMutationResult<PaymentOnboardingResponse, unknown, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post(`${base(businessId)}/refresh`, {}, paymentOnboardingResponseSchema),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.owner.payoutAccount(businessId), data);
    },
  });
}
